//import { isDate, parse } from "date-fns";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { tz } from "../../common/constants";
import { hasResourceRole } from './tokenUtil';
import { CostCenterDef, GenListType, InternalOrderDef } from '../types';


dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(tz);
export class wdpUtil {

  static getUniqueId(): string {
    return crypto.randomUUID();
  }

  
  // Normalizes a partially/loosely typed "HH:mm" time input (used by the Start/Stop time fields
  // in WorkInfo.tsx and TravelInfo.tsx) into a well-formed "HH:mm" string on blur. Always ensures
  // a ":" is present in the result - if the user only typed the hour part (with or without a
  // trailing ":"), the minutes default to "00" instead of being left off.
  static normalizeTypedTimeInput(value: string): string {
    const raw = String(value || "").trim();
    if (!raw) return "";

    if (raw.includes(":")) {
      const [hourPart = "", minutePart = ""] = raw.split(":", 2);
      const hourDigits = hourPart.replace(/\D/g, "").slice(0, 2);
      const minuteDigits = minutePart.replace(/\D/g, "").slice(0, 2);

      if (!hourDigits) return "";
      if (!minuteDigits) return `${hourDigits.padStart(2, "0")}:00`;

      return `${hourDigits.padStart(2, "0")}:${minuteDigits.padEnd(2, "0")}`;
    }

    const digits = raw.replace(/\D/g, "").slice(0, 4);
    if (!digits) return "";
    if (digits.length <= 2) return `${digits.padStart(2, "0")}:00`;
    if (digits.length === 3) return `0${digits.slice(0, 1)}:${digits.slice(1)}`;

    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }

  static isNullorUndefined(obj: any): boolean {
    return (obj === null || obj === undefined || (typeof obj === 'string' && obj === ""));
  }

  static safeParse(value: any, options?: { type?: "int" | "float" }): number {
    const { type = "int" } = options || {};
    // Handle nullish values
    if (value === null || value === undefined) {
      return 0;
    }

    // Convert to string safely
    const str = String(value).trim();

    // Empty or whitespace-only
    if (str === "") {
      return 0;
    }

    // Choose parser
    const parsed = type === "int" ? parseInt(str, 10) : parseFloat(str);

    // NaN check
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return parsed;
  }

  static sortData(sortBy: string, sortDir: number, data: any): any[] {
    const _data = [...data];
    if (data != null && data.length > 0) {
      _data.sort((a: any, b: any) => {
        let aval = a[sortBy];
        let bval = b[sortBy];

        aval = this.isNullorUndefined(a[sortBy]) ? '' : a[sortBy];
        bval = this.isNullorUndefined(b[sortBy]) ? '' : b[sortBy];


        const parts = sortBy.split('.');
        if (parts !== null && parts.length === 2) {
          // handle nested value
          aval = this.isNullorUndefined(a[parts[0]]) ? '' : this.isNullorUndefined(a[parts[0]][parts[1]]) ? '' : a[parts[0]][parts[1]];
          bval = this.isNullorUndefined(b[parts[0]]) ? '' : this.isNullorUndefined(b[parts[0]][parts[1]]) ? '' : b[parts[0]][parts[1]];
        }
        // assuming aval and bval are string
        let res = 1;
        if (typeof aval === 'string' && typeof bval === 'string') {
          res = (aval.toLowerCase() > bval.toLowerCase()) ? 1 : -1;
        }

        else {
          res = (aval > bval) ? 1 : -1;
        }
        return res * sortDir;
      });
    }
    return _data;
  }
  
  static sortOptList(who: string, mylist: any[]) {
    
    if (this.isNullorUndefined(mylist)) {
      return mylist;
    }

    if (who === 'internalOrder') {
      return [...mylist].sort((a: InternalOrderDef, b: InternalOrderDef) => {
        const aKey = a.internalOrderDescription.trimStart().toLowerCase();
        const bKey = b.internalOrderDescription.trimStart().toLowerCase();
        return aKey.localeCompare(bKey);
      });
    }
    if (who === 'costCenter') {
      return [...mylist].sort((a: CostCenterDef, b: CostCenterDef) => {
        const aKey = a.costCenterDescription.trimStart().toLowerCase();
        const bKey = b.costCenterDescription.trimStart().toLowerCase();
        return aKey.localeCompare(bKey);
      });
    }

    return [...mylist].sort((a: GenListType, b: GenListType) =>a.label.localeCompare(b.label));
  }

  static deepCopy(src: any) {
    const asStr = JSON.stringify(src);
    return JSON.parse(asStr);
  }
  
  static deepMerge(target: any, source: any) {
    const output = { ...target };

    Object.keys(source).forEach(key => {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        output[key] = wdpUtil.deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    });

    return output;
  }

  static formatDate(dt: string | null|undefined, ret: string = '', includeTime: boolean = false) {
    let res = ret;
    if (wdpUtil.isNullorUndefined(dt)) {
      return res;
    }

    const parsed = dayjs(dt);
    if (!parsed.isValid()) {
      return res;
    }

    // Use standard date-time format that Dayjs can parse without custom plugins.
    res = includeTime ? parsed.tz().format('ddd, MMM D, YYYY h:mm A') : parsed.format('YYYY-MM-DD');
    return res;
  }

  static toIsoStringOrEmpty(value?: string | null): string {
    if (wdpUtil.isNullorUndefined(value)) {
      return "";
    }

    const normalized = String(value).trim();
    if (normalized === "") {
      return "";
    }

    const parsed = dayjs(normalized);
    return parsed.isValid() ? parsed.toISOString() : "";
  }

  static daysDiff(dt1: Date, dt2: Date): number {
    if (wdpUtil.isNullorUndefined(dt1) || wdpUtil.isNullorUndefined(dt2)) {
      return 0;
    }
    const dt1Timestamp = dt1.getTime();
    const dt2Timestamp = dt2.getTime();
    const dif = dt2Timestamp - dt1Timestamp;
    var daysDifference = Math.round(dif / (1000 * 3600 * 24));
    return daysDifference;
  }
  static isDST(val: string | null) {
    if (val === null) {
      return 0;
    }
    const utcTime = dayjs(val);
    const Edmonton = dayjs(val).tz();
    const DST = Edmonton.get('day') < utcTime.get('day');
    return (DST) ? 1 : 0;
  }

  static getTimezoneAbbreviation(dateStr: string): string {
    const isDaylight = wdpUtil.isDST(dateStr);
    return isDaylight ? 'MDT' : 'MST';
  }

  static formatNoteTimestamp(timestamp: string): string {
    if (wdpUtil.isNullorUndefined(timestamp)) {
      return '';
    }
    const date = dayjs(timestamp).tz();
    const formattedDate = date.format('dddd, MMMM D, YYYY');
    const formattedTime = date.format('h:mm:ss A');
    const timezone = wdpUtil.getTimezoneAbbreviation(timestamp);
    return `On ${formattedDate} at ${formattedTime} (${timezone})`;
  }
  
  static getErrMsg(errCode: number) {
    switch (errCode) {
      case 404:
        return "Error: Page not found";
        break;
      case 500:
        return "Error: Internal server error";
    }
  }
  static numberWithCommas(x: number | string) {
    //const ret:string = x.toLocaleString("en-US", { minimumFractionDigits: 2 });// doesnt work
    //return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
    let res = '';
    // make it string
    let xStr = (typeof x === 'string') ? x : x.toString();
    // cleanup
    // remove commas
    //remove leading zeros (ifany)by using parsefloat
    xStr = xStr.trim();
    xStr = parseFloat(wdpUtil.replaceAll(xStr, ',', '')).toString();

    if (xStr !== '') {
      xStr = wdpUtil.replaceAll(xStr, ',', '');
      res = xStr.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");

      const pos = res.indexOf('.');
      if (pos === -1) {
        res += '.00';
      } else {
        const howMay = res.slice(pos + 1).length;
        if (howMay < 2)
          res += howMay === 0 ? '00' : '0';
      }

    }

    return res;
  }
  // NOTE: since 2021, Javascript intriduced replaceAll fucntrion , but it doesnt work for IE
  static replaceAll(src: string, find: string, replace: string) {
    //return src.replace(`/${find}/g`, replace);
    if (!wdpUtil.isNullorUndefined(src))
      return src.replace(new RegExp(find, "g"), replace);
    return '';
  }


  static isNullOrEmpty(item: string | GenListType | null): boolean {
    let ret = true;

    if (item != null && (typeof item == 'string') && item != '') {
      ret = false;
    } else {
      if (item != null && (item as GenListType).label != null && (item as GenListType).label != '') {
        ret = false;
      }
    }
    return ret;
  }
  static formatValFloat(item: string | GenListType | number | null, needLabel: boolean = false): string {
    // Nullish → empty
    if (item === null || item === undefined) {
      return "";
    }

    let raw: any = item;

    // If GenListType → choose label or value
    if (typeof item === "object" && "label" in item && "value" in item) {
      raw = needLabel ? item.label : item.value;
    }

    // Normalize to string
    const str = String(raw).trim();

    // Empty or whitespace → empty
    if (str === "") {
      return "";
    }

    // Strict numeric parsing
    const num = Number(str);

    // Invalid number → empty
    if (!Number.isFinite(num)) {
      return "";
    }

    // Format to 2 decimals
    return num.toFixed(2);
  }
  // Converts decimal hours (e.g. "8.50") into "H:MM" format (e.g. "8:30").
  static formatHoursToHM(item: string | number | null): string {
    if (item === null || item === undefined) {
      return "";
    }
    const str = String(item).trim();
    if (str === "") {
      return "";
    }
    const totalHours = Number(str);
    if (!Number.isFinite(totalHours)) {
      return "";
    }
    const sign = totalHours < 0 ? "-" : "";
    const absHours = Math.abs(totalHours);
    const hours = Math.floor(absHours);
    const minutes = Math.round((absHours - hours) * 60);
    // guard against rounding minutes up to 60 (e.g. 7.999 -> 8:00, not 7:60)
    const carry = minutes === 60 ? 1 : 0;
    const finalHours = hours + carry;
    const finalMinutes = carry ? 0 : minutes;
    return `${sign}${finalHours}:${finalMinutes.toString().padStart(2, "0")}`;
  }
  // Sanitizes a raw numeric input string: strips non-digit/non-dot characters, collapses
  // multiple decimal points down to the first one, and truncates (does not round) to at
  // most `maxDecimals` digits after the decimal point. Intended for use in onChange handlers
  // to restrict typing as the user types, e.g. "1.23456" -> "1.23".
  // `maxIntegerDigits` (optional) additionally caps the number of digits before the decimal
  // point, e.g. maxIntegerDigits=3 restricts typing to "xxx.xx". Left undefined by default so
  // existing callers that only care about decimals are unaffected.
  static restrictDecimalInput(value: string | null | undefined, maxDecimals: number = 2, maxIntegerDigits?: number): string {
    let numericValue = value?.replace(/[^0-9.]/g, '') ?? '';

    const firstDotIndex = numericValue.indexOf('.');
    if (firstDotIndex !== -1) {
      numericValue = numericValue.slice(0, firstDotIndex + 1)
        + numericValue.slice(firstDotIndex + 1).replace(/\./g, '');

      if (numericValue.length > firstDotIndex + 1 + maxDecimals) {
        numericValue = numericValue.slice(0, firstDotIndex + 1 + maxDecimals);
      }
    }

    if (maxIntegerDigits !== undefined) {
      const dotIndex = numericValue.indexOf('.');
      const integerPart = dotIndex === -1 ? numericValue : numericValue.slice(0, dotIndex);
      if (integerPart.length > maxIntegerDigits) {
        const trimmedIntegerPart = integerPart.slice(0, maxIntegerDigits);
        numericValue = dotIndex === -1 ? trimmedIntegerPart : trimmedIntegerPart + numericValue.slice(dotIndex);
      }
    }

    return numericValue;
  }
  static isEqualGenListType(a: GenListType|null, b: GenListType|null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.value === b.value && a.label === b.label;
  }
  static isEmptyGenListType(a: GenListType | null | undefined): boolean {
    if (!a) return true; // null or undefined
    return (a.value === "" || a.value == null) &&
          (a.label === "" || a.label == null);
  }
  //**************************************************************************************
  //* 
  //* @param item 
  //* @param needlabel 
  //* @returns 
  //*****************************************************************************************/
  static getFormFieldVal(item: string | GenListType | null | undefined, needlabel: boolean = false): string {
    let ret = '';

    if (item != null) {
      if ((typeof item == 'string')) {
        ret = item;
      } else {
        if (needlabel == true && (item as GenListType).label != null && (item as GenListType).label != '') {
          ret = (item as GenListType).label;
        }
        if (needlabel == false && (item as GenListType).value != null && (item as GenListType).value != '') {
          ret = (item as GenListType).value;
        }
      }
    }
    return ret;
  }

  static findDuplicatesByProperty(arr: Object[], property: keyof Object): Object[] {
    const seen = new Map<any, boolean>(); // Stores seen property values
    const duplicates: Object[] = [];

    for (const item of arr) {
      const value = item[property];
      if (seen.has(value)) {
        duplicates.push(item); // Found a duplicate
      } else {
        seen.set(value, true);
      }
    }
    return duplicates;
  }
  static getUniqueByProperty(arr: any, prop: any) {
    const uniqueMap = new Map();
    for (const item of arr) {
      // If the ID is not already a key in the Map, add it. 
      // This keeps the first occurrence and ignores subsequent duplicates.
      if (!uniqueMap.has(item[prop])) {
        uniqueMap.set(item[prop], item);
      }
    }
    // Convert the Map values back to an array
    return Array.from(uniqueMap.values());
  }
  static calcCost(ratePerUnit: number, noOfUnits: number): string {
    let ret = '';
    if (!Number.isNaN(ratePerUnit) && !Number.isNaN(noOfUnits)) {
      const value = (ratePerUnit ?? 0) * (noOfUnits ?? 0);
      // Clean up floating-point multiplication noise (e.g. 149.99999999999997 instead of 150)
      // before rounding up to the next cent. Note: do NOT nudge with Number.EPSILON here - adding
      // it to an exact 0 produces a tiny positive number that Math.ceil rounds up to 1 cent.
      const cents = Math.round(value * 100 * 1e6) / 1e6;
      const roundedUp = Math.ceil(cents) / 100;
      ret = roundedUp.toFixed(2);

    }
    return ret;
  }

  static inListCI(value: string | null | undefined, list: string[]): boolean {
    if (!value) return false;
    const lower = value.toLowerCase();
    return list.some(item => item.toLowerCase() === lower);
  }

  static hasAppPermission(auth: any, permissionName: string) {
    return auth.isAuthenticated && hasResourceRole('wildfire-time-tracking-api', [permissionName], auth.user?.access_token as string);
  }
  static downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  static downloadFromUrl(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  static strEqual(_str1: string | number, _str2: string | number, casesensitive: boolean = false) {
    const str1 = typeof _str1 == "number" ? _str1.toString() : _str1;
    const str2 = typeof _str2 == "number" ? _str2.toString() : _str2;

    if (str1 != null && str2 != null) {
      if (casesensitive)
        return (str1 === str2);
      else
        return (str1.toLowerCase() === str2.toLowerCase());
    }
    return false;
  }
  static includesIgnoreCase(source?: string[] | string, search?: string): boolean {
    if (!source || !search) return false;
    if (typeof source === 'string') {
      return source.toLowerCase().includes(search.toLowerCase());
    }
    else {
      const target = search.toLowerCase();
      return source.some(item => item?.toLowerCase() === target);
    }
  }

  // recurasive
  static deepReset<T>(obj: T, resetValue: any): T {
  if (Array.isArray(obj)) {
    return obj.map(item => wdpUtil.deepReset(item, resetValue)) as T;
  }

  if (obj !== null && typeof obj === "object") {
    const out: any = {};
    for (const key of Object.keys(obj)) {
      out[key] = wdpUtil.deepReset((obj as any)[key], resetValue);
    }
    return out;
  }

  return resetValue;
}




}
